import { Controller } from "@hotwired/stimulus"

// Shows the estimated driving time from the booking employee's Standort to the selected
// Baustelle, as read-only information beneath each site select.
//
// The figure depends on BOTH selections: the site, and which employees the entry is being
// booked for (admins and proxy users pick several, who may sit at different Standorte). So
// this refetches on either change rather than rendering once at form render time.
//
// Mounted once on the form, not per row, because the employee picker is form-level while a
// form can carry SEVERAL site selects: one per entry block (multi-entry creation) or one
// per allocation (multi-site booking). Rows are therefore paired by position —
// siteSelectTargets[i] belongs to containerTargets[i] — which holds because each row
// renders exactly one of each, in DOM order.
//
// Everything is resolved and formatted server-side — the Standort fallback chain lives in
// HeadquartersResolver and must not be reimplemented here.
//
// On a Sammelkostenstelle the drive goes to the row's Einsatzort, not the site's address
// (SiteDrivingTimes::DestinationLookup). The row's work_location_controller announces every
// settled change with "work-location:changed", and its fields ride along on the request.
const NO_CREDIT = Object.freeze({ drive_to_minutes: 0, drive_from_minutes: 0 })

export default class extends Controller {
  static targets = [
    "container",
    "output",
    "siteSelect",
    "employeeSelect",
    "compensation",
    "driveTo",
    "driveFrom",
    "credit",
  ]
  // addedOnTop mirrors the resolved Timesheet::DrivingTimeCompensationSettings mode:
  // false = the credit is carved out of the entered window, true = it is paid on top.
  static values = { url: String, templateId: Number, addedOnTop: Boolean }

  initialize() {
    // Last request key per row index, so a change on one row does not refetch the others
    // and a repeated change event (SlimSelect re-renders) costs nothing.
    this.keys = new Map()
    // Creditable minutes per direction, per row index, as last reported by the server.
    // Never computed here: the rounding and the deduction live in
    // SiteDrivingTimes::CompensationCalculator and must have exactly one home.
    this.credits = new Map()
    this.locationChanged = this.locationChanged.bind(this)
  }

  connect() {
    this.element.addEventListener("work-location:changed", this.locationChanged)
  }

  disconnect() {
    this.element.removeEventListener("work-location:changed", this.locationChanged)
  }

  // The Einsatzort block sits in the same row as its site select.
  locationChanged(event) {
    const index = this.siteSelectTargets.findIndex((select) => event.target.contains(select))
    if (index >= 0) this.refreshAt(index)
  }

  // Both multi-entry and multi-site add rows by cloning the first one, which copies the
  // source row's rendered figure while clearing its site select. Re-sync the new row so a
  // stale number never sits under a blank Baustelle.
  containerTargetConnected(element) {
    this.refreshAt(this.containerTargets.indexOf(element))
  }

  containerTargetDisconnected() {
    // Positions shift when a row is removed; drop the cache rather than let it describe
    // the wrong rows.
    this.keys.clear()
    this.credits.clear()
    this.announceCredit()
  }

  // A site change touches only its own row; an employee change touches all of them.
  refresh(event) {
    const index = this.siteSelectTargets.indexOf(event?.target)

    if (index >= 0) {
      this.refreshAt(index)
    } else {
      this.refreshAll()
    }
  }

  refreshAll() {
    this.siteSelectTargets.forEach((_, index) => this.refreshAt(index))
  }

  refreshAt(index) {
    // The site select carries this controller's target unconditionally, but the block is
    // only rendered when the template opts in. Without this guard every site change on
    // every timesheet form in the product would fetch an answer with nowhere to put it.
    if (index < 0 || !this.hasContainerTarget) return

    const select = this.siteSelectTargets[index]
    if (!select) return

    const siteId = select.value
    const employeeIds = this.selectedEmployeeIds()
    const location = this.workLocationAt(select)
    const key = `${siteId}|${employeeIds.join(",")}|${new URLSearchParams(location).toString()}`

    // No site, nothing to attribute a driving time to. A form that HAS an employee picker
    // but nothing picked is the same case: the server would fall back to the signed-in
    // account, so an admin would see their own Standort under an entry booked for someone
    // else. A plain form has no picker and legitimately resolves to its own employee.
    if (!siteId || (this.hasEmployeeSelectTarget && employeeIds.length === 0)) {
      this.keys.set(index, key)
      this.clearAt(index)
      return
    }

    if (this.keys.get(index) === key) return
    this.keys.set(index, key)

    this.load(index, siteId, employeeIds, location, key)
  }

  async load(index, siteId, employeeIds, location, key) {
    const params = new URLSearchParams()
    params.append("template_id", this.templateIdValue)
    params.append("site_id", siteId)
    employeeIds.forEach((id) => params.append("employee_ids[]", id))
    Object.entries(location).forEach(([name, value]) => params.append(name, value))

    try {
      const response = await fetch(`${this.urlValue}?${params.toString()}`, {
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
      })
      // A refusal is an expected answer, not a fault: an employee who records times purely
      // via the check-in has no manual create permission. The block is informational, so it
      // simply stays empty rather than logging on every site change.
      if (!response.ok) {
        this.clearAt(index)
        return
      }

      const data = await response.json()
      // A slower earlier request must not overwrite a newer selection.
      if (this.keys.get(index) !== key) return

      this.keepGeocodedLocation(this.siteSelectTargets[index], location, data.work_location)
      this.render(index, data.rows || [])
    } catch (error) {
      console.error("Failed to load site driving times:", error)
      this.clearAt(index)
    }
  }

  render(index, rows) {
    const output = this.outputTargets[index]
    const container = this.containerTargets[index]
    if (!output || !container) return

    if (rows.length === 0) {
      this.clearAt(index)
      return
    }

    output.innerHTML = rows.map((row) => this.rowHtml(row, output)).join("")
    container.classList.remove("hidden")
    this.renderCompensation(index, rows)
  }

  // The direction checkboxes, shown only once the server has reported creditable minutes
  // for this row. Several rows mean the selected employees sit at different Standorte and
  // would each earn a different credit — one pair of checkboxes cannot express that, so
  // the choice is withdrawn rather than silently applying one person's figure to all.
  //
  // An Einsatzort whose drive could not be determined ("unavailable") keeps its checkboxes
  // at 0 minutes: the saved entry credits 0 for now, and the server snapshots the credit
  // again once the provider answers — clearing the claim here would lose it for good.
  renderCompensation(index, rows) {
    const block = this.compensationTargets[index]
    if (!block) return

    const row = rows.length === 1 ? rows[0] : null
    const usable = row && (row.compensation || (row.unavailable ? NO_CREDIT : null))
    if (!usable) {
      this.credits.delete(index)
      this.resetCompensation(index)
      block.classList.add("hidden")
      this.announceCredit()
      return
    }

    this.credits.set(index, usable)
    block.classList.remove("hidden")
    this.updateCredit(index)
  }

  // A checkbox was toggled: repaint that row's figure and let the multi-site controller
  // re-derive how much is still to be distributed across sites.
  creditChanged(event) {
    const index = this.indexOfRow(event?.target)
    if (index >= 0) this.updateCredit(index)
    this.announceCredit()
  }

  updateCredit(index) {
    const output = this.creditTargets[index]
    if (!output) return

    const minutes = this.creditMinutesAt(index)
    if (minutes <= 0) {
      output.textContent = ""
      return
    }

    const template = output.dataset.creditedTemplate || "%{value}"
    output.textContent = template.replace("%{value}", this.formatHours(minutes))
  }

  // Minutes this row currently claims: the sum of the directions actually ticked.
  creditMinutesAt(index) {
    const credit = this.credits.get(index)
    if (!credit) return 0

    const driveTo = this.driveToTargets[index]
    const driveFrom = this.driveFromTargets[index]
    return (
      (driveTo?.checked ? Number(credit.drive_to_minutes) || 0 : 0) +
      (driveFrom?.checked ? Number(credit.drive_from_minutes) || 0 : 0)
    )
  }

  totalCreditMinutes() {
    return this.compensationTargets.reduce(
      (total, _, index) => total + this.creditMinutesAt(index),
      0,
    )
  }

  // Multi-site booking needs the credit to work out the hours still to allocate. Announced
  // as an event so this controller keeps owning the figure and the other one stays free of
  // any knowledge of how it is derived.
  //
  // Two numbers, because they answer two different questions. creditMinutes is what the day
  // credits in total and is what the user is shown. minutesInsideWindow is how much of that
  // has to fit within the entered time span — the whole credit when it is carved out, and
  // nothing when it is paid on top, where the sites cover the window by themselves. Mirrors
  // ConfigurableTimeSheets::AllocationDrivingCredit#minutes_inside_window.
  announceCredit() {
    const creditMinutes = this.totalCreditMinutes()

    this.element.dispatchEvent(
      new CustomEvent("site-driving-time:credit-changed", {
        bubbles: true,
        detail: {
          creditMinutes,
          minutesInsideWindow: this.addedOnTopValue ? 0 : creditMinutes,
        },
      }),
    )
  }

  resetCompensation(index) {
    const driveTo = this.driveToTargets[index]
    const driveFrom = this.driveFromTargets[index]
    const output = this.creditTargets[index]

    if (driveTo) driveTo.checked = false
    if (driveFrom) driveFrom.checked = false
    if (output) output.textContent = ""
  }

  // Which row a checkbox belongs to. The pairs render in DOM order alongside the
  // containers, so their positions line up.
  indexOfRow(element) {
    const container = element?.closest("[data-site-driving-time-target~='container']")
    return container ? this.containerTargets.indexOf(container) : -1
  }

  // German decimal-comma hours ("1,5h"), matching SiteDrivingTimesHelper#german_decimal_hours.
  formatHours(minutes) {
    const hours = minutes / 60
    const rounded = Math.round(hours * 100) / 100
    return `${rounded
      .toFixed(2)
      .replace(/\.?0+$/, "")
      .replace(".", ",")}h`
  }

  rowHtml(row, output) {
    const names =
      row.employee_names && row.employee_names.length > 0
        ? `<span class="block text-xs text-gray-400">${this.escape(row.employee_names.join(", "))}</span>`
        : ""
    const manual = row.manual
      ? `<span class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">${this.escape(output.dataset.manualLabel || "")}</span>`
      : ""

    return `<div class="flex items-baseline justify-between gap-3 py-0.5">
        <span class="min-w-0 text-gray-500">
          <span class="block truncate">${this.escape(row.company_location_name)}</span>
          ${names}
        </span>
        <span class="shrink-0 font-medium text-gray-700 tabular-nums">${this.escape(row.label)}${manual}</span>
      </div>`
  }

  clearAt(index) {
    const output = this.outputTargets[index]
    const container = this.containerTargets[index]
    const compensation = this.compensationTargets[index]

    if (output) output.innerHTML = ""
    if (container) container.classList.add("hidden")

    if (compensation) {
      this.credits.delete(index)
      this.resetCompensation(index)
      compensation.classList.add("hidden")
      this.announceCredit()
    }
  }

  // The Einsatzort fields of the row holding +select+, as request params. Disabled inputs
  // are skipped: work_location_controller disables the block while the site is not a
  // Sammelkostenstelle, and a hidden leftover must not steer the drive.
  workLocationAt(select) {
    const block = select.closest("[data-controller~='work-location']")
    if (!block) return {}

    const fields = {
      work_location_latitude: "latitude",
      work_location_longitude: "longitude",
      work_location_address: "address",
      site_location_id: "siteLocation",
    }
    return Object.entries(fields).reduce((params, [name, target]) => {
      const input = this.workLocationInput(block, target)
      if (input && !input.disabled && input.value) params[name] = input.value
      return params
    }, {})
  }

  workLocationInput(block, target) {
    return block.querySelector(`[data-work-location-target~='${target}']`)
  }

  // The server geocoded a typed address: keep its coordinates on the form so the save
  // routes to exactly the place the preview showed — unless the row moved on meanwhile.
  keepGeocodedLocation(select, location, resolved) {
    if (!resolved || !select) return

    const block = select.closest("[data-controller~='work-location']")
    const address = block && this.workLocationInput(block, "address")
    const latitude = block && this.workLocationInput(block, "latitude")
    const longitude = block && this.workLocationInput(block, "longitude")
    if (!address || !latitude || !longitude) return
    if (address.value !== location.work_location_address || latitude.value || longitude.value)
      return

    latitude.value = resolved.latitude
    longitude.value = resolved.longitude
  }

  selectedEmployeeIds() {
    if (!this.hasEmployeeSelectTarget) return []

    const select = this.employeeSelectTarget
    if (select.multiple) {
      return Array.from(select.selectedOptions)
        .map((option) => option.value)
        .filter(Boolean)
    }
    return select.value ? [select.value] : []
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : value
    return div.innerHTML
  }
}
